import { dateToFrDate } from '@/app/utils/health/health-date-utils'
import {
    LOAN_MAX_PERIOD_MONTHS,
    LOAN_SIGNATURE_MAX_FUTURE_MONTHS,
    PROJECT_TYPE_SWITCHING,
    needsSwitching,
} from '@/app/constants/loanTypes'

// Date de signature : règles répliquées du parcours legacy Symfony (LoanComparatorFinancingForm.vue).
export type LoanSignatureCase = 'newLoan' | 'switching' | 'repurchase'

// Le rachat (is_loan_repurchase) exige une date future : min = demain, contrairement au nouveau prêt.
export const getLoanSignatureCase = (
    quote?: { type_quote?: string; project_progression?: string; is_loan_repurchase?: boolean } | null,
): LoanSignatureCase => {
    const isSwitching =
        needsSwitching(quote?.type_quote) || quote?.project_progression === PROJECT_TYPE_SWITCHING
    if (isSwitching) return 'switching'
    if (quote?.is_loan_repurchase) return 'repurchase'
    return 'newLoan'
}

const startOfToday = (): Date => {
    const d = new Date()
    d.setHours(0, 0, 0, 0)
    return d
}

const shiftDays = (base: Date, days: number): Date => {
    const d = new Date(base)
    d.setDate(d.getDate() + days)
    return d
}

const shiftMonths = (base: Date, months: number): Date => {
    const d = new Date(base)
    d.setMonth(d.getMonth() + months)
    return d
}

const parseFrDate = (value: string): Date | null => {
    if (!/^\d{2}\/\d{2}\/\d{4}$/.test(value)) return null
    const [day, month, year] = value.split('/').map(Number)
    const d = new Date(year, month - 1, day)
    // Reject impossible dates: new Date() normalizes overflow (e.g. 31/02 → 03/03).
    if (d.getFullYear() !== year || d.getMonth() !== month - 1 || d.getDate() !== day) return null
    d.setHours(0, 0, 0, 0)
    return d
}

// newLoan → [aujourd'hui, +12 mois] ; repurchase → [demain, +12 mois] ; switching → [-480 mois, hier]
export const getLoanSignatureDateBounds = (
    signatureCase: LoanSignatureCase,
): { minDate: string; maxDate: string } => {
    const today = startOfToday()
    if (signatureCase === 'switching') {
        return {
            minDate: dateToFrDate(shiftMonths(today, -LOAN_MAX_PERIOD_MONTHS)),
            maxDate: dateToFrDate(shiftDays(today, -1)),
        }
    }
    const maxDate = dateToFrDate(shiftMonths(today, LOAN_SIGNATURE_MAX_FUTURE_MONTHS))
    if (signatureCase === 'repurchase') {
        return { minDate: dateToFrDate(shiftDays(today, 1)), maxDate }
    }
    return { minDate: dateToFrDate(today), maxDate }
}

export const isLoanSignatureDateValid = (value: string, signatureCase: LoanSignatureCase): boolean => {
    const date = parseFrDate(value)
    if (!date) return false
    const today = startOfToday()
    if (signatureCase === 'switching') {
        return date >= shiftMonths(today, -LOAN_MAX_PERIOD_MONTHS) && date <= shiftDays(today, -1)
    }
    const maxDate = shiftMonths(today, LOAN_SIGNATURE_MAX_FUTURE_MONTHS)
    const minDate = signatureCase === 'repurchase' ? shiftDays(today, 1) : today
    return date >= minDate && date <= maxDate
}
